import type { LucideIcon } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { BentoCard } from "@/components/ui/BentoCard";
import { cn } from "@/lib/utils";

/**
 * Shared anatomy for every card on the Integrations page:
 *
 *   [ icon tile ] → [ title + status pill ] → [ description ]
 *   → flex spacer → [ hairline divider ] → [ footer action ]
 *
 * The skeleton being identical across apps, channels, and states is what
 * makes a grid of mixed integrations read as one designed set — keep all
 * state variation inside the pill and footer slots, never the layout.
 */

interface IntegrationCardShellProps {
  icon: LucideIcon;
  title: string;
  pill?: ReactNode;
  description?: string;
  /** Optional mid-card content (address row, setup steps). Keep it quiet. */
  children?: ReactNode;
  /** Footer-pinned action slot; omit to render no divider or footer. */
  footer?: ReactNode;
}

export function IntegrationCardShell({
  icon: Icon,
  title,
  pill,
  description,
  children,
  footer,
}: IntegrationCardShellProps) {
  return (
    <BentoCard hover className="rounded-3xl p-6">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-home-ink/[0.05] text-home-ink dark:bg-white/10 dark:text-white">
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="font-body text-[15px] font-semibold text-home-ink dark:text-white">
          {title}
        </h3>
        {pill}
      </div>

      {description && (
        <p className="mt-1.5 line-clamp-2 font-body text-[13px] leading-relaxed text-home-ink/55 dark:text-white/55">
          {description}
        </p>
      )}

      {children}

      {footer && (
        <div className="mt-auto pt-5">
          <div className="border-t border-home-ink/8 pt-4 dark:border-white/10">
            {footer}
          </div>
        </div>
      )}
    </BentoCard>
  );
}

type StatusPillVariant = "active" | "attention" | "pending" | "muted";

const PILL_STYLES: Record<StatusPillVariant, string> = {
  active: "bg-accent-green/15 text-accent-green",
  attention: "bg-home-terracotta/12 text-home-terracotta",
  pending: "bg-home-butter text-home-ink/75",
  muted: "bg-home-ink/[0.06] text-home-ink/55 dark:bg-white/10 dark:text-white/55",
};

interface StatusPillProps {
  variant: StatusPillVariant;
  children: ReactNode;
  /** Animate the dot — for live/active states only. */
  pulse?: boolean;
}

export function StatusPill({ variant, children, pulse = false }: StatusPillProps) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 font-body text-[11px] font-semibold whitespace-nowrap",
        PILL_STYLES[variant],
      )}
    >
      {(variant === "active" || variant === "attention") && (
        <span
          className={cn("h-1.5 w-1.5 rounded-full bg-current", pulse && "animate-pulse")}
        />
      )}
      {children}
    </span>
  );
}

/**
 * tonal — the default Connect action; quiet enough that a wall of cards
 *   doesn't shout.
 * primary — home-yellow, reserved for "action needed to make this work"
 *   (reconnect after error, create the missing inbox). One warm moment.
 * quiet — text-only, for disconnect/manage.
 * danger — tonal terracotta, for the destructive confirm step.
 */
type CardActionTone = "tonal" | "primary" | "quiet" | "danger";

const ACTION_STYLES: Record<CardActionTone, string> = {
  tonal:
    "w-full rounded-full bg-home-ink/[0.05] px-4 py-2.5 font-semibold text-home-ink hover:bg-home-ink/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/15",
  primary:
    "w-full rounded-full bg-home-yellow px-4 py-2.5 font-bold text-home-ink shadow-[0_8px_20px_-10px_rgba(33,31,28,0.35)] hover:-translate-y-px hover:brightness-95 disabled:hover:translate-y-0",
  quiet:
    "rounded-full px-3 py-2 font-medium text-home-ink/45 hover:bg-home-ink/[0.04] hover:text-home-ink dark:text-white/45 dark:hover:bg-white/5 dark:hover:text-white",
  danger:
    "rounded-full bg-home-terracotta/10 px-4 py-2 font-semibold text-home-terracotta hover:bg-home-terracotta/20",
};

interface CardActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: CardActionTone;
}

export function CardActionButton({
  tone = "tonal",
  className,
  type = "button",
  ...rest
}: CardActionButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "font-body text-sm transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40",
        ACTION_STYLES[tone],
        className,
      )}
      {...rest}
    />
  );
}

/**
 * Two-step disconnect, shared by every connected card. Raw provider errors
 * never render here or anywhere on the page — they stay in Convex logs.
 */
interface DisconnectFooterProps {
  confirming: boolean;
  disconnecting: boolean;
  onConfirmChange: (confirming: boolean) => void;
  onDisconnect: () => void;
}

export function DisconnectFooter({
  confirming,
  disconnecting,
  onConfirmChange,
  onDisconnect,
}: DisconnectFooterProps) {
  if (!confirming) {
    return (
      <div className="flex justify-start">
        <CardActionButton tone="quiet" onClick={() => onConfirmChange(true)}>
          Disconnect
        </CardActionButton>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="mr-auto font-body text-[13px] text-home-ink/55 dark:text-white/55">
        Remove this connection?
      </span>
      <CardActionButton tone="danger" disabled={disconnecting} onClick={onDisconnect}>
        {disconnecting ? "Removing…" : "Yes"}
      </CardActionButton>
      <CardActionButton tone="quiet" onClick={() => onConfirmChange(false)}>
        Cancel
      </CardActionButton>
    </div>
  );
}
